import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bot, Building2, CheckCircle2, ChevronDown, ChevronUp, Clock, ExternalLink, FileText, HandMetal, Link2, Loader2, MessageSquare, Shield, Upload, XCircle } from "lucide-react";
import { DetailRow, DetailRows } from "@/components/shared/DetailRows";
import { RequestDetailLayout } from "@/components/shared/RequestDetailLayout";
import { UserAvatar } from "@/components/ui/user-avatar";
import toast from "react-hot-toast";

import { LoadingPage } from "@/components/shared/LoadingPage";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useEnterprise } from "@/contexts/EnterpriseContext";
import {
  getStaffRequest,
  getStaffChildRequests,
  staffCompleteStep,
  staffPickUpRequest,
  staffRejectRequest,
  staffRequestInfo,
  staffUploadDocument,
  staffUploadCollectionDocument,
  type ChildRequestSummary,
  type ChildRequestDocument,
  getStaffRequestParticipants,
  type StaffRequestParticipant,
} from "@/services/enterpriseRequestsService";
import { AuthenticatedBlobDocumentLink } from "@/components/shared/AuthenticatedBlobDocumentLink";
import { StatusBadge, type StatusBadgeVariant } from "@/components/shared/StatusBadge";
import {
  RequestActivityList,
  enterpriseActivityConfig,
} from "@/components/shared/RequestActivityList";
import { CompletedStepsSummary } from "@/components/features/enterprise/CompletedStepsSummary";
import type {
  GraphState,
  ProductGraph,
  ProductNode,
} from "@/services/platformEnterprisesService";
import {
  getStaffRequestEvents,
  fetchStaffDocumentBlob,
} from "@/services/enterpriseRequestsService";
import { evaluateRateTable } from "@/utils/enterprisePricingEngine";
import { getActorLabel, AUTOMATION_TYPES } from "@gestionalo/enterprise-graph";
import type { PricingNodeData, AutomationNodeData, DocumentCollectionNodeData } from "@gestionalo/enterprise-graph";
import { apiClient } from "@/services/apiClient";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-UY", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusLabel(
  status: string,
  t: (k: string) => string,
): string {
  const KEY_MAP: Record<string, string> = {
    pending: "enterpriseStaff.requests.statusPending",
    in_review: "enterpriseStaff.requests.statusInReview",
    approved: "enterpriseStaff.requests.statusApproved",
    rejected: "enterpriseStaff.requests.statusRejected",
    completed: "enterpriseStaff.requests.statusCompleted",
    cancelled: "enterpriseStaff.requests.statusCancelled",
    needs_info: "enterpriseStaff.requests.statusNeedsInfo",
    awaiting_action: "enterpriseStaff.requests.statusAwaitingAction",
  };
  return t(KEY_MAP[status] ?? status);
}

const STATUS_BADGE_VARIANT: Record<string, StatusBadgeVariant> = {
  pending: "warning",
  in_review: "info",
  awaiting_action: "info",
  approved: "success",
  rejected: "softDanger",
  completed: "success",
  cancelled: "softDanger",
  needs_info: "danger",
  awaiting_child: "info",
};

function applicantDisplayName(req: { userId: string; user?: { firstName?: string | null; lastName?: string | null; email: string } | null }): string {
  const u = req.user;
  if (!u) return req.userId.slice(0, 8);
  const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return name || u.email;
}

function computePricingBreakdown(graph: ProductGraph, state: GraphState, payload: Record<string, unknown>) {
  const pricingNode = state.completedPath
    .map((id) => graph.nodes.find((n) => n.id === id))
    .find((n) => n?.type === "pricing");
  if (!pricingNode) return null;

  const formula = (pricingNode.data as unknown as PricingNodeData).formula;
  const flat: Record<string, unknown> = {};
  for (const value of Object.values(payload)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      Object.assign(flat, value);
    }
  }
  const result = evaluateRateTable(formula, flat);
  if ("error" in result) return null;
  return result;
}

export function EnterpriseStaffRequestDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { subdomain } = useEnterprise();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const requestQuery = useQuery({
    queryKey: ["enterprise-staff-request", subdomain, id],
    queryFn: () => getStaffRequest(subdomain, id!),
    enabled: Boolean(id),
  });

  const childRequestsQuery = useQuery({
    queryKey: ["enterprise-staff-request-children", subdomain, id],
    queryFn: () => getStaffChildRequests(subdomain, id!),
    enabled: Boolean(id) && Boolean(requestQuery.data?.workflowState),
  });

  const participantsQuery = useQuery({
    queryKey: ["enterprise-staff-request-participants", subdomain, id],
    queryFn: () => getStaffRequestParticipants(subdomain, id!),
    enabled: Boolean(id),
  });

  const eventsQuery = useQuery({
    queryKey: ["enterprise-staff-request-events", subdomain, id],
    queryFn: () => getStaffRequestEvents(subdomain, id!),
    enabled: Boolean(id),
  });

  const rejectMutation = useMutation({
    mutationFn: () => {
      const reqId = requestQuery.data?.id;
      if (!reqId) return Promise.reject(new Error('No request'));
      return staffRejectRequest(subdomain, reqId, rejectReason || undefined);
    },
    onSuccess: () => {
      toast.success(t("enterpriseStaff.requests.actionSuccess"));
      setRejectDialogOpen(false);
      invalidateRequest();
    },
    onError: () => toast.error(t("enterpriseStaff.requests.actionError")),
  });

  const invalidateRequest = () => {
    void queryClient.invalidateQueries({ queryKey: ["enterprise-staff-request", subdomain, id] });
    void queryClient.invalidateQueries({ queryKey: ["enterprise-staff-requests", subdomain] });
    void queryClient.invalidateQueries({ queryKey: ["enterprise-staff-request-events", subdomain, id] });
    void queryClient.invalidateQueries({ queryKey: ["enterprise-staff-request-participants", subdomain, id] });
  };

  useEffect(() => {
    if (requestQuery.isError) {
      toast.error(t("enterpriseStaff.requests.loadError"));
    }
  }, [requestQuery.isError, t]);

  if (requestQuery.isLoading) return <LoadingPage />;

  const req = requestQuery.data;
  if (!req) {
    return (
      <div className="p-4 md:p-8">
        <p className="text-muted-foreground">{t("enterpriseStaff.requests.notFound")}</p>
      </div>
    );
  }

  const hasParent = req.parentRequestId && req.parentRequestType;
  const graph = (req as { graph?: ProductGraph | null }).graph ?? null;
  const state = req.workflowState as GraphState | null;
  const hasWorkflow = Boolean(graph && state);
  const payload = req.payload as Record<string, unknown> | null;

  const currentStep =
    graph && state?.currentNodeId
      ? graph.nodes.find((n) => n.id === state.currentNodeId)
      : undefined;

  // Resolve actor label for current step (e.g. "Arrendador" for participant steps)
  const currentStepActorLabel = (() => {
    if (!currentStep || !graph) return undefined;
    const actorSource = (currentStep.data as { actorSource?: string } | undefined)?.actorSource;
    if (actorSource) {
      const inviteNode = graph.nodes.find((n) => n.id === actorSource && n.type === "inviteActors");
      return ((inviteNode?.data as { actorLabel?: string } | undefined)?.actorLabel?.trim()) || undefined;
    }
    return undefined;
  })();

  const isTerminal = ["completed", "rejected", "cancelled"].includes(req.status);

  const rejectionEvent = req.status === "rejected"
    ? (eventsQuery.data ?? []).find((e) => e.type === "rejected")
    : null;
  const rejectionReason = rejectionEvent?.data?.reason as string | undefined;

  const pricingBreakdown = graph && state && payload
    ? computePricingBreakdown(graph, state, payload)
    : null;

  // Extract uploaded documents from payload (manual uploads, file fields, documentCollection)
  const uploadedDocuments = payload
    ? Object.entries(payload).flatMap(([nId, nodeData]) => {
        if (!nodeData || typeof nodeData !== "object" || Array.isArray(nodeData)) return [];
        const d = nodeData as Record<string, unknown>;
        const actorMatch = nId.match(/^(.+)_(\d+)$/);
        const baseId = actorMatch ? actorMatch[1] : nId;
        const actorIdx = actorMatch ? Number(actorMatch[2]) : null;
        const node = graph?.nodes.find((n) => n.id === baseId) ?? graph?.nodes.find((n) => n.id === nId);
        const baseLabel = (node?.data as { label?: string } | undefined)?.label ?? baseId;
        const actorLbl = graph ? getActorLabel(graph) : "Participante";
        const nodeLabel = actorIdx !== null
          ? `${baseLabel} — ${actorLbl} ${actorIdx + 1}`
          : baseLabel;
        const pathIndex = state?.completedPath.indexOf(baseId) ?? -1;

        // Single-file upload (old format: { fileUrl, fileName })
        if (d.fileUrl && d.fileName) {
          return [{ nodeId: nId, nodeLabel, fileName: d.fileName as string, pathIndex }];
        }

        // documentCollection or userForm with file fields: nested objects/URLs
        const results: Array<{ nodeId: string; nodeLabel: string; fileName: string; pathIndex: number }> = [];

        // Build a lookup of document key → human label from documentCollection config
        const docLabelMap = new Map<string, string>();
        if (node?.type === "documentCollection") {
          const docData = node.data as { documentSets?: Record<string, { documents?: Array<{ key: string; label: string }> }> };
          for (const ds of Object.values(docData.documentSets ?? {})) {
            for (const doc of ds.documents ?? []) {
              docLabelMap.set(doc.key, doc.label);
            }
          }
        }

        for (const [key, val] of Object.entries(d)) {
          if (val && typeof val === "object" && !Array.isArray(val)) {
            const sub = val as Record<string, unknown>;
            if (sub.fileUrl && sub.fileName) {
              // documentCollection format: { docKey: { fileUrl, fileName } }
              const docLabel = docLabelMap.get(key) ?? key;
              results.push({ nodeId: nId, nodeLabel: docLabel, fileName: sub.fileName as string, pathIndex });
            }
          } else if (typeof val === "string" && val.startsWith("https://") && val.includes("blob")) {
            // userForm file field: plain URL string
            const fieldDef = node?.type === "userForm"
              ? ((node.data as { fields?: Array<{ key: string; label: string }> }).fields ?? []).find((f) => f.key === key)
              : null;
            const label = fieldDef?.label ?? key;
            results.push({ nodeId: nId, nodeLabel: label, fileName: label, pathIndex });
          }
        }
        return results;
      }).sort((a, b) => b.pathIndex - a.pathIndex)
    : [];

  // Include info reply attachments
  if (payload?.__infoReplyAttachments) {
    const attachments = payload.__infoReplyAttachments as Array<{ label: string; fileUrl: string; fileName: string }>;
    for (let i = 0; i < attachments.length; i++) {
      const att = attachments[i];
      uploadedDocuments.push({
        nodeId: `__infoReply_${i}`,
        nodeLabel: att.label || att.fileName,
        fileName: att.fileName,
        pathIndex: -1,
      });
    }
  }

  // Rejecting is only offered while the request can still move. Built here
  // rather than inline in the prop, per the project rule against multi-line
  // logic in JSX props.
  const rejectAction = isTerminal ? null : (
    <Button
      type="button"
      variant="destructive"
      size="sm"
      onClick={() => setRejectDialogOpen(true)}
      className="gap-2"
    >
      <XCircle className="h-4 w-4" />
      {t("enterpriseStaff.requests.actionReject")}
    </Button>
  );

  return (
    <RequestDetailLayout
      title={t("enterpriseStaff.requests.detailTitle")}
      onBack={() => navigate("/requests")}
      actions={rejectAction}
    >

      {/* Two-column layout: main content + activity sidebar */}
      <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
        {/* Main column */}
        <div className="min-w-0 space-y-4">
          {/* Details card */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">
                  {req.product?.displayName ?? req.productSlug}
                </CardTitle>
                <StatusBadge variant={STATUS_BADGE_VARIANT[req.status] ?? "info"}>
                  {statusLabel(req.status, t)}
                </StatusBadge>
              </div>
            </CardHeader>
            <CardContent>
              <DetailRows>
                <DetailRow
                  label={t("enterpriseStaff.requests.columns.applicant")}
                  valueClassName="font-medium"
                >
                  {applicantDisplayName(req)}
                </DetailRow>
                {req.user?.email && (
                  <DetailRow label="Email">{req.user.email}</DetailRow>
                )}
                <DetailRow label={t("enterpriseStaff.requests.timestamps.created")}>
                  {formatDateTime(req.createdAt)}
                </DetailRow>
                {hasParent && (
                  <DetailRow
                    label={t("enterpriseStaff.requests.parentLinkLabel")}
                    valueClassName="flex items-center gap-1.5"
                  >
                    <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{req.parentRequestType} ({req.parentRequestId!.slice(0, 8)})</span>
                  </DetailRow>
                )}
              </DetailRows>
            </CardContent>
          </Card>

          {/* Rejection reason */}
          {req.status === "rejected" && (
            <Card className="border-destructive/20 bg-destructive/5 shadow-sm">
              <CardContent className="flex items-start gap-3 py-5">
                <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold text-foreground">
                    {t("requests.user.enterprise.rejectedTitle")}
                  </h3>
                  {rejectionReason && (
                    <p className="text-sm text-muted-foreground">{rejectionReason}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pricing breakdown — right below header */}
          {pricingBreakdown && (
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {t("requests.user.wizard.pricingSummaryTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DetailRows>
                  {pricingBreakdown.breakdown.map((line, idx) => {
                    const isLast = idx === pricingBreakdown.breakdown.length - 1;
                    return (
                      <div
                        key={idx}
                        className={`grid grid-cols-[minmax(0,180px)_1fr] gap-3 py-1.5 first:pt-0 ${
                          isLast ? "font-semibold" : ""
                        }`}
                      >
                        <dt className={`text-sm ${isLast ? "text-foreground" : "text-muted-foreground"}`}>
                          {line.label}
                        </dt>
                        <dd className={`text-sm text-right ${isLast ? "text-foreground" : "text-foreground"}`}>
                          {line.value}
                        </dd>
                      </div>
                    );
                  })}
                </DetailRows>
              </CardContent>
            </Card>
          )}

          {/* Datos + Participantes side by side */}
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Completed steps data */}
            {graph && state && payload && (
              <CompletedStepsSummary
                graph={graph}
                workflowState={state}
                payload={payload}
              />
            )}

            {/* Participants (requester + all invited actors with BCU results) */}
            {participantsQuery.data && (
              <ParticipantsCard
                participants={participantsQuery.data.filter((p) => p.role === "participant")}
                graph={graph}
                requester={req.user}
                workflowState={state}
              />
            )}
          </div>

          {/* Completed automation results (single-actor format only — multi-actor shown in participants card) */}
          {hasWorkflow && graph && state && graph.nodes
            .filter((n) => {
              if (n.type !== "automation") return false;
              const stepData = state.completedNodes[n.id]?.data as Record<string, unknown> | undefined;
              if (!stepData) return false;
              // Multi-actor results (stored as .results) are shown in ParticipantsCard
              if (stepData.results) return false;
              // Single-actor results (stored as .result)
              return stepData.automationStatus === "success" && stepData.result;
            })
            .map((node) => {
              const stepData = state.completedNodes[node.id].data as Record<string, unknown>;
              const nodeData = node.data as unknown as AutomationNodeData;
              return (
                <CompletedAutomationResult
                  key={node.id}
                  label={nodeData.label}
                  result={stepData.result as Record<string, unknown>}
                />
              );
            })}

          {/* Uploaded documents */}
          {uploadedDocuments.length > 0 && (
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {t("enterpriseStaff.requests.documentsTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("enterpriseStaff.requests.docColumnStep")}</TableHead>
                      <TableHead>{t("enterpriseStaff.requests.docColumnFile")}</TableHead>
                      <TableHead className="w-[100px]">{t("requests.user.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {uploadedDocuments.map((doc, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Badge variant="secondary" className="font-normal normal-case">
                            {doc.nodeLabel}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-foreground">
                          {doc.fileName}
                        </TableCell>
                        <TableCell>
                          <AuthenticatedBlobDocumentLink
                            fetchBlob={() => fetchStaffDocumentBlob(subdomain, req.id, doc.nodeId)}
                            title={doc.fileName}
                            className="inline-flex h-8 items-center rounded-md border border-input bg-background px-3 text-xs font-medium text-foreground no-underline shadow-sm hover:bg-accent hover:text-accent-foreground"
                          >
                            {t("enterpriseStaff.requests.viewDocument")}
                          </AuthenticatedBlobDocumentLink>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Participants — moved to top of page, see above */}

          {/* Child requests (linked certificates etc.) */}
          {childRequestsQuery.data && childRequestsQuery.data.length > 0 && (
            <ChildRequestsCard childRequests={childRequestsQuery.data} />
          )}


        </div>

        {/* Right sidebar: actions + activity */}
        <div className="space-y-4">
          {/* Staff actions */}
          {hasWorkflow && !isTerminal && currentStep?.type === "automation" && (
            <AutomationActionCard
              subdomain={subdomain}
              requestId={req.id}
              currentStep={currentStep}
              allPayloadValues={payload ?? {}}
              onActionComplete={invalidateRequest}
            />
          )}
          {hasWorkflow && !isTerminal && currentStep?.type === "documentCollection" && (
            <DocumentCollectionActionCard
              subdomain={subdomain}
              requestId={req.id}
              currentStep={currentStep}
              allPayloadValues={payload ?? {}}
              onActionComplete={invalidateRequest}
            />
          )}
          {hasWorkflow && !isTerminal && currentStep?.type !== "automation" && currentStep?.type !== "documentCollection" && (
            <StaffActionsCard
              subdomain={subdomain}
              requestId={req.id}
              enterpriseId={req.enterpriseId}
              currentStep={currentStep}
              currentStepLabel={currentStep ? (currentStep.data as { label?: string }).label : undefined}
              currentStepActorLabel={currentStepActorLabel}
              isAwaitingChild={req.status === "awaiting_child"}
              isPending={req.status === "pending"}
              onActionComplete={invalidateRequest}
            />
          )}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {t("requests.user.activityHistoryTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {eventsQuery.isLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <RequestActivityList
                  events={(eventsQuery.data ?? []).map((e) => ({
                    id: e.id,
                    type: e.type,
                    createdAt: e.createdAt,
                    actorUserId: e.actorUserId,
                    data: e.data,
                  }))}
                  config={enterpriseActivityConfig(graph)}
                  emptyMessage={t("enterpriseStaff.requests.activityEmpty")}
                />
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("enterpriseStaff.requests.rejectTitle")}</DialogTitle>
            <DialogDescription>{t("enterpriseStaff.requests.rejectDescription")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t("enterpriseStaff.requests.rejectReasonLabel")}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => rejectMutation.mutate()}
              disabled={rejectMutation.isPending}
            >
              {rejectMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("enterpriseStaff.requests.actionReject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </RequestDetailLayout>
  );
}

function formatUYU(amount: number): string {
  return amount.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ratingColor(rating: string): { bg: string; text: string; border: string } {
  const num = parseInt(rating, 10) || 0;
  if (num <= 1) return { bg: "bg-green-50 dark:bg-green-950/30", text: "text-green-700 dark:text-green-400", border: "border-green-200 dark:border-green-900" };
  if (num === 2) return { bg: "bg-amber-50 dark:bg-amber-950/30", text: "text-amber-700 dark:text-amber-400", border: "border-amber-200 dark:border-amber-900" };
  return { bg: "bg-red-50 dark:bg-red-950/30", text: "text-red-700 dark:text-red-400", border: "border-red-200 dark:border-red-900" };
}

interface BcuBreakdown {
  vigenteMN: number;
  vigenteME: number;
  contingenciasMN: number;
  contingenciasME: number;
  garantiasMN: number;
  garantiasME: number;
}

interface BcuInstitution {
  name: string;
  rating: string;
  breakdown: BcuBreakdown;
}

function generatePeriodoOptions(): Array<{ value: string; label: string }> {
  const options: Array<{ value: string; label: string }> = [];
  const now = new Date();
  const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    options.push({
      value: `${yyyy}${mm}`,
      label: `${monthNames[d.getMonth()]} ${yyyy}`,
    });
  }
  return options;
}

function PeriodoSelect({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}): JSX.Element {
  const options = generatePeriodoOptions();
  return (
    <Select value={value || options[0].value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-9 text-sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function CompletedAutomationResult({
  label,
  result,
}: {
  label: string;
  result: Record<string, unknown>;
}): JSX.Element {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [expandedInst, setExpandedInst] = useState<Set<number>>(new Set());

  const hasDebts = result.hasDebts as boolean;
  const personName = (result.personName as string) ?? "";
  const activitySector = (result.activitySector as string) ?? "";
  const worstRating = (result.worstRating as string) ?? "";
  const summary = (result.summary ?? {}) as BcuBreakdown;
  const institutions = (result.institutions ?? []) as BcuInstitution[];
  const periodo = (result.periodo as string) ?? "";
  const periodoFormatted = periodo.length === 6 ? `${periodo.slice(4, 6)}/${periodo.slice(0, 4)}` : periodo;

  const toggleInst = (idx: number) => {
    setExpandedInst((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const rc = worstRating ? ratingColor(worstRating) : { bg: "bg-green-50 dark:bg-green-950/30", text: "text-green-700 dark:text-green-400", border: "border-green-200 dark:border-green-900" };

  return (
    <Card className="shadow-sm">
      <CardContent className="space-y-3 py-4">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center gap-2 text-left"
        >
          <h3 className="text-sm font-semibold flex-1">{label}</h3>
          <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
          {expanded
            ? <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
            : <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          }
        </button>

        {!hasDebts ? (
          <div className="rounded-md border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30 p-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-700 dark:text-green-400">
                  {t("enterpriseStaff.requests.bcuNoDebts")}
                </p>
                {personName && <p className="text-xs text-green-600">{personName}</p>}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className={`rounded-lg border ${rc.border} ${rc.bg} p-3`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  {personName && <p className="text-sm font-semibold">{personName}</p>}
                  {activitySector && <p className="text-[11px] text-muted-foreground">{activitySector}</p>}
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {t("enterpriseStaff.requests.bcuPeriodo")}: {periodoFormatted}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-center gap-0.5">
                  <div className="flex items-center gap-1">
                    <Shield className={`h-3.5 w-3.5 ${rc.text}`} />
                    <span className="text-[10px] text-muted-foreground">{t("enterpriseStaff.requests.bcuRating")}</span>
                  </div>
                  <span className={`rounded-md px-2.5 py-0.5 text-base font-bold ${rc.bg} ${rc.text} border ${rc.border}`}>
                    {worstRating}
                  </span>
                </div>
              </div>
            </div>

            {expanded && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg border bg-card p-2.5">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{t("enterpriseStaff.requests.bcuVigente")}</p>
                    <p className="mt-0.5 text-sm font-bold">$ {formatUYU(summary.vigenteMN)}</p>
                    {summary.vigenteME > 0 && <p className="text-[11px] text-muted-foreground">ME $ {formatUYU(summary.vigenteME)}</p>}
                  </div>
                  <div className="rounded-lg border bg-card p-2.5">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{t("enterpriseStaff.requests.bcuContingencias")}</p>
                    <p className="mt-0.5 text-sm font-bold">$ {formatUYU(summary.contingenciasMN)}</p>
                    {summary.contingenciasME > 0 && <p className="text-[11px] text-muted-foreground">ME $ {formatUYU(summary.contingenciasME)}</p>}
                  </div>
                </div>

                {institutions.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("enterpriseStaff.requests.bcuInstitutions")} ({institutions.length})
                    </p>
                    {institutions.map((inst, idx) => {
                      const instRc = ratingColor(inst.rating);
                      const instExpanded = expandedInst.has(idx);
                      const instTotal = inst.breakdown.vigenteMN + inst.breakdown.vigenteME;
                      return (
                        <div key={idx} className="rounded-lg border bg-card">
                          <button
                            type="button"
                            onClick={() => toggleInst(idx)}
                            className="flex w-full items-center gap-2 p-2 text-left transition-colors hover:bg-muted/50"
                          >
                            <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-medium">{inst.name}</p>
                              <p className="text-[11px] text-muted-foreground">$ {formatUYU(instTotal)}</p>
                            </div>
                            <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${instRc.bg} ${instRc.text} border ${instRc.border}`}>
                              {inst.rating}
                            </span>
                            {instExpanded
                              ? <ChevronUp className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              : <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            }
                          </button>
                          {instExpanded && (
                            <div className="border-t px-2 pb-2 pt-1.5">
                              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">{t("enterpriseStaff.requests.bcuVigente")} MN</span>
                                  <span className="font-mono font-medium">$ {formatUYU(inst.breakdown.vigenteMN)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">{t("enterpriseStaff.requests.bcuVigente")} ME</span>
                                  <span className="font-mono font-medium">$ {formatUYU(inst.breakdown.vigenteME)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">{t("enterpriseStaff.requests.bcuContingencias")} MN</span>
                                  <span className="font-mono font-medium">$ {formatUYU(inst.breakdown.contingenciasMN)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">{t("enterpriseStaff.requests.bcuContingencias")} ME</span>
                                  <span className="font-mono font-medium">$ {formatUYU(inst.breakdown.contingenciasME)}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DocumentCollectionActionCard({
  subdomain,
  requestId,
  currentStep,
  allPayloadValues,
  onActionComplete,
}: {
  subdomain: string;
  requestId: string;
  currentStep: ProductNode;
  allPayloadValues: Record<string, unknown>;
  onActionComplete: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const data = currentStep.data as unknown as DocumentCollectionNodeData;

  const participantsQuery = useQuery({
    queryKey: ["staff-doc-collection-participants", subdomain, requestId],
    queryFn: () => getStaffRequestParticipants(subdomain, requestId),
    enabled: !!data.actorSource,
  });

  const actors = data.actorSource
    ? (participantsQuery.data ?? []).filter((p) => {
        if (p.role === "initiator") return true;
        return p.status === "accepted" && p.inviteNodeId === data.actorSource;
      })
    : [{ name: "Solicitante", role: "initiator" as const }];

  const queryClient = useQueryClient();
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);

  const handleUpload = async (actorIndex: number, documentKey: string, file: File) => {
    const nodeId = data.actorSource ? `${currentStep.id}_${actorIndex}` : currentStep.id;
    setUploadingDoc(`${actorIndex}_${documentKey}`);
    try {
      await staffUploadCollectionDocument(subdomain, requestId, nodeId, documentKey, file);
      void queryClient.invalidateQueries({ queryKey: ["staff-request"] });
      onActionComplete();
    } catch {
      toast.error(t("enterpriseStaff.requests.actionError"));
    } finally {
      setUploadingDoc(null);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4 text-purple-600" />
          {data.label || t("enterpriseStaff.requests.documentCollection.title")}
        </CardTitle>
        {data.description && (
          <p className="text-xs text-muted-foreground">{data.description}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {actors.map((actor, actorIndex) => {
          // Per-actor precondition fields are always stored as key_N,
          // so try actor-indexed key first, then base key as fallback.
          const keysToTry = [
            `${data.conditionField}_${actorIndex}`,
            data.conditionField,
          ];
          let conditionValue = "";
          outerLoop: for (const key of keysToTry) {
            for (const nodePayload of Object.values(allPayloadValues)) {
              if (nodePayload && typeof nodePayload === "object" && !Array.isArray(nodePayload)) {
                const val = (nodePayload as Record<string, unknown>)[key];
                if (val != null) {
                  conditionValue = String(val);
                  break outerLoop;
                }
              }
            }
          }
          const docSet = data.documentSets[conditionValue];
          if (!docSet) return null;

          const payloadKey = data.actorSource
            ? `${currentStep.id}_${actorIndex}`
            : currentStep.id;
          const uploadedDocs = (allPayloadValues[payloadKey] ?? {}) as Record<
            string,
            { fileUrl?: string; fileName?: string }
          >;
          const requiredDocs = docSet.documents.filter((d) => d.required);
          const uploadedCount = requiredDocs.filter(
            (d) => uploadedDocs[d.key]?.fileUrl,
          ).length;
          const allDone = uploadedCount === requiredDocs.length;

          return (
            <div key={actorIndex} className="space-y-2 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium">
                    {"user" in actor && actor.user
                      ? `${actor.user.firstName ?? ""} ${actor.user.lastName ?? ""}`.trim() || actor.email
                      : "name" in actor
                        ? actor.name
                        : `Actor ${actorIndex}`}
                  </span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {docSet.label}
                  </span>
                </div>
                <Badge variant={allDone ? "default" : "secondary"} className="text-[10px]">
                  {uploadedCount}/{requiredDocs.length}
                </Badge>
              </div>

              {docSet.documents.map((doc) => {
                const uploaded = uploadedDocs[doc.key];
                const isUploading = uploadingDoc === `${actorIndex}_${doc.key}`;

                return (
                  <div
                    key={doc.key}
                    className="flex items-center gap-2 rounded border px-3 py-2"
                  >
                    {uploaded?.fileUrl ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                    ) : (
                      <div className="h-4 w-4 shrink-0 rounded-full border-2 border-muted-foreground/30" />
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium">{doc.label}</span>
                      {doc.required && !uploaded?.fileUrl && (
                        <span className="ml-1 text-[10px] text-destructive">*</span>
                      )}
                      {uploaded?.fileName && (
                        <p className="text-[10px] text-muted-foreground truncate">
                          {uploaded.fileName}
                        </p>
                      )}
                    </div>
                    {uploaded?.fileUrl ? (
                      <AuthenticatedBlobDocumentLink
                        fetchBlob={() => fetchStaffDocumentBlob(subdomain, requestId, data.actorSource ? `${currentStep.id}_${actorIndex}` : currentStep.id)}
                        className="text-[10px]"
                      >
                        {t("enterpriseStaff.requests.documentCollection.view")}
                      </AuthenticatedBlobDocumentLink>
                    ) : (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) void handleUpload(actorIndex, doc.key, file);
                            e.target.value = "";
                          }}
                          disabled={isUploading}
                        />
                        <span className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-medium hover:bg-muted transition-colors">
                          {isUploading ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Upload className="h-3 w-3" />
                          )}
                          {t("enterpriseStaff.requests.documentCollection.upload")}
                        </span>
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function AutomationActionCard({
  subdomain,
  requestId,
  currentStep,
  allPayloadValues,
  onActionComplete,
}: {
  subdomain: string;
  requestId: string;
  currentStep: ProductNode;
  allPayloadValues: Record<string, unknown>;
  onActionComplete: () => void;
}): JSX.Element {
  const data = currentStep.data as unknown as AutomationNodeData;
  const typeDef = AUTOMATION_TYPES.find((at) => at.type === data.automationType);

  if (data.actorSource) {
    return (
      <MultiActorAutomationCard
        subdomain={subdomain}
        requestId={requestId}
        currentStep={currentStep}
        data={data}
        typeDef={typeDef}
        onActionComplete={onActionComplete}
      />
    );
  }

  return (
    <SingleActorAutomationCard
      subdomain={subdomain}
      requestId={requestId}
      currentStep={currentStep}
      data={data}
      typeDef={typeDef}
      allPayloadValues={allPayloadValues}
      onActionComplete={onActionComplete}
    />
  );
}

function SingleActorAutomationCard({
  subdomain,
  requestId,
  currentStep,
  data,
  typeDef,
  allPayloadValues,
  onActionComplete,
}: {
  subdomain: string;
  requestId: string;
  currentStep: ProductNode;
  data: AutomationNodeData;
  typeDef: (typeof AUTOMATION_TYPES)[number] | undefined;
  allPayloadValues: Record<string, unknown>;
  onActionComplete: () => void;
}): JSX.Element {
  const { t } = useTranslation();

  const [running, setRunning] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [result, setResult] = useState<{
    automationStatus?: string;
    automationError?: string;
    automationMessage?: string;
    result?: Record<string, unknown>;
  } | null>(null);
  const [runtimeInputs, setRuntimeInputs] = useState<Record<string, string>>({});
  const [expandedInst, setExpandedInst] = useState<Set<number>>(new Set());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const runtimeFields = (typeDef?.inputs ?? []).filter((inputDef) => {
    const mapping = data.inputMappings[inputDef.key];
    return !mapping || mapping.length === 0;
  });

  const allRequiredFilled = runtimeFields
    .filter((f) => f.required)
    .every((f) => runtimeInputs[f.key]?.trim());

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const handleRun = async () => {
    setRunning(true);
    setElapsedSec(0);
    setResult(null);
    timerRef.current = setInterval(() => setElapsedSec((s) => s + 1), 1000);
    try {
      const res = await apiClient.post(
        `/enterprise/me/requests/${requestId}/run-automation/${currentStep.id}`,
        { runtimeInputs },
        { headers: { "X-Enterprise-Subdomain": subdomain } },
      );
      setResult({
        automationStatus: res.data.status,
        automationError: res.data.error,
        automationMessage: res.data.message,
        result: res.data.data,
      });
      if (res.data.status === "success") {
        // Delay the refetch so the user can see the results
        setTimeout(() => onActionComplete(), 500);
      }
    } catch {
      setResult({
        automationStatus: "error",
        automationError: "network_error",
        automationMessage: t("enterpriseStaff.requests.automationNetworkError"),
      });
    } finally {
      setRunning(false);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
  };

  const toggleInst = (idx: number) => {
    setExpandedInst((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const bcuResult = result?.result;
  const hasDebts = bcuResult?.hasDebts as boolean | undefined;
  const personName = (bcuResult?.personName as string) ?? "";
  const activitySector = (bcuResult?.activitySector as string) ?? "";
  const worstRating = (bcuResult?.worstRating as string) ?? "";
  const summary = (bcuResult?.summary ?? {}) as BcuBreakdown;
  const institutions = (bcuResult?.institutions ?? []) as BcuInstitution[];
  const periodo = (bcuResult?.periodo as string) ?? "";
  const periodoFormatted = periodo.length === 6 ? `${periodo.slice(4, 6)}/${periodo.slice(0, 4)}` : periodo;

  const errorMessages: Record<string, string> = {
    captcha_failed: t("enterpriseStaff.requests.bcuErrorCaptcha"),
    login_failed: t("enterpriseStaff.requests.bcuErrorLogin"),
    timeout: t("enterpriseStaff.requests.bcuErrorTimeout"),
    parse_error: t("enterpriseStaff.requests.bcuErrorParse"),
    not_configured: t("enterpriseStaff.requests.bcuErrorNotConfigured"),
    service_unavailable: t("enterpriseStaff.requests.bcuErrorUnavailable"),
    network_error: t("enterpriseStaff.requests.automationNetworkError"),
  };

  return (
    <Card className="shadow-sm border-fuchsia-200 dark:border-fuchsia-900 bg-fuchsia-50/50 dark:bg-fuchsia-950/20">
      <CardContent className="space-y-4 py-5">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-fuchsia-100 dark:bg-fuchsia-900/30">
            <Bot className="h-4 w-4 text-fuchsia-600 dark:text-fuchsia-400" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-foreground">{data.label}</h3>
            {data.description && (
              <p className="text-xs text-muted-foreground">{data.description}</p>
            )}
          </div>
        </div>

        {/* Pre-run: explanation */}
        {!result && !running && (
          <div className="rounded-md border border-fuchsia-200 dark:border-fuchsia-800 bg-fuchsia-50 dark:bg-fuchsia-950/30 p-3">
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t("enterpriseStaff.requests.automationExplain")}
            </p>
          </div>
        )}

        {/* Mapped inputs (read-only) */}
        {(typeDef?.inputs ?? [])
          .filter((i) => (data.inputMappings[i.key] ?? "").length > 0)
          .map((inputDef) => {
            const mappedKey = data.inputMappings[inputDef.key];
            const value = String(allPayloadValues[mappedKey] ?? "");
            return (
              <div key={inputDef.key}>
                <Label className="text-xs text-muted-foreground">{inputDef.label}</Label>
                <p className="text-sm font-medium">{value || "\u2014"}</p>
              </div>
            );
          })}

        {/* Runtime inputs */}
        {!result && !running && runtimeFields.map((inputDef) => (
          <div key={inputDef.key} className="space-y-1">
            <Label className="text-xs">
              {inputDef.label}
              {inputDef.required && <span className="ml-1 text-destructive">*</span>}
            </Label>
            {inputDef.key === "periodo" ? (
              <PeriodoSelect
                value={runtimeInputs[inputDef.key] ?? ""}
                onChange={(v) => setRuntimeInputs((prev) => ({ ...prev, [inputDef.key]: v }))}
                disabled={running}
              />
            ) : (
              <Input
                value={runtimeInputs[inputDef.key] ?? ""}
                onChange={(e) =>
                  setRuntimeInputs((prev) => ({ ...prev, [inputDef.key]: e.target.value }))
                }
                disabled={running}
                placeholder={inputDef.defaultDescription}
              />
            )}
          </div>
        ))}

        {/* Running state */}
        {running && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 rounded-md border border-fuchsia-200 dark:border-fuchsia-800 bg-fuchsia-50 dark:bg-fuchsia-950/30 p-3">
              <Loader2 className="h-5 w-5 shrink-0 animate-spin text-fuchsia-600" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {t("enterpriseStaff.requests.automationRunningTitle")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {elapsedSec < 10
                    ? t("enterpriseStaff.requests.automationStepLogin")
                    : elapsedSec < 30
                    ? t("enterpriseStaff.requests.automationStepCaptcha")
                    : elapsedSec < 60
                    ? t("enterpriseStaff.requests.automationStepQuery")
                    : t("enterpriseStaff.requests.automationStepWaiting")}
                </p>
              </div>
              <span className="ml-auto shrink-0 text-xs font-mono text-muted-foreground">
                {elapsedSec}s
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-fuchsia-100 dark:bg-fuchsia-900/30">
              <div
                className="h-full rounded-full bg-fuchsia-500 transition-all duration-1000"
                style={{ width: `${Math.min((elapsedSec / 90) * 100, 95)}%` }}
              />
            </div>
          </div>
        )}

        {/* Run / Retry button */}
        {!running && (!result || result.automationStatus === "error") && (
          <Button
            onClick={handleRun}
            disabled={!allRequiredFilled}
            className="w-full bg-fuchsia-600 hover:bg-fuchsia-700"
            size="sm"
          >
            {result?.automationStatus === "error"
              ? t("enterpriseStaff.requests.automationRetry")
              : t("enterpriseStaff.requests.automationRun")}
          </Button>
        )}

        {/* Error display */}
        {!running && result && result.automationStatus === "error" && (
          <div className="rounded-md border border-destructive/20 bg-destructive/5 p-3 space-y-1">
            <p className="text-xs font-medium text-destructive">
              {errorMessages[result.automationError ?? ""] ?? result.automationError}
            </p>
            {result.automationMessage && result.automationMessage !== result.automationError && (
              <p className="text-[11px] text-destructive/70">{result.automationMessage}</p>
            )}
          </div>
        )}

        {/* Success: No debts */}
        {result && result.automationStatus === "success" && hasDebts === false && (
          <div className="rounded-md border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30 p-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-700 dark:text-green-400">
                  {t("enterpriseStaff.requests.bcuNoDebts")}
                </p>
                {personName && <p className="text-xs text-green-600">{personName}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Success: Has debts — credit report */}
        {result && result.automationStatus === "success" && hasDebts === true && (() => {
          const rc = ratingColor(worstRating);
          return (
            <div className="space-y-3">
              <div className={`rounded-lg border ${rc.border} ${rc.bg} p-3`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    {personName && <p className="text-sm font-semibold">{personName}</p>}
                    {activitySector && <p className="text-[11px] text-muted-foreground">{activitySector}</p>}
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {t("enterpriseStaff.requests.bcuPeriodo")}: {periodoFormatted}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-center gap-0.5">
                    <div className="flex items-center gap-1">
                      <Shield className={`h-3.5 w-3.5 ${rc.text}`} />
                      <span className="text-[10px] text-muted-foreground">{t("enterpriseStaff.requests.bcuRating")}</span>
                    </div>
                    <span className={`rounded-md px-2.5 py-0.5 text-base font-bold ${rc.bg} ${rc.text} border ${rc.border}`}>
                      {worstRating}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg border bg-card p-2.5">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{t("enterpriseStaff.requests.bcuVigente")}</p>
                  <p className="mt-0.5 text-sm font-bold">$ {formatUYU(summary.vigenteMN)}</p>
                  {summary.vigenteME > 0 && <p className="text-[11px] text-muted-foreground">ME $ {formatUYU(summary.vigenteME)}</p>}
                </div>
                <div className="rounded-lg border bg-card p-2.5">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{t("enterpriseStaff.requests.bcuContingencias")}</p>
                  <p className="mt-0.5 text-sm font-bold">$ {formatUYU(summary.contingenciasMN)}</p>
                  {summary.contingenciasME > 0 && <p className="text-[11px] text-muted-foreground">ME $ {formatUYU(summary.contingenciasME)}</p>}
                </div>
              </div>

              {institutions.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t("enterpriseStaff.requests.bcuInstitutions")} ({institutions.length})
                  </p>
                  {institutions.map((inst, idx) => {
                    const instRc = ratingColor(inst.rating);
                    const expanded = expandedInst.has(idx);
                    const instTotal = inst.breakdown.vigenteMN + inst.breakdown.vigenteME;
                    return (
                      <div key={idx} className="rounded-lg border bg-card">
                        <button
                          type="button"
                          onClick={() => toggleInst(idx)}
                          className="flex w-full items-center gap-2 p-2 text-left transition-colors hover:bg-muted/50"
                        >
                          <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-medium">{inst.name}</p>
                            <p className="text-[11px] text-muted-foreground">$ {formatUYU(instTotal)}</p>
                          </div>
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${instRc.bg} ${instRc.text} border ${instRc.border}`}>
                            {inst.rating}
                          </span>
                          {expanded
                            ? <ChevronUp className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            : <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          }
                        </button>
                        {expanded && (
                          <div className="border-t px-2 pb-2 pt-1.5">
                            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">{t("enterpriseStaff.requests.bcuVigente")} MN</span>
                                <span className="font-mono font-medium">$ {formatUYU(inst.breakdown.vigenteMN)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">{t("enterpriseStaff.requests.bcuVigente")} ME</span>
                                <span className="font-mono font-medium">$ {formatUYU(inst.breakdown.vigenteME)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">{t("enterpriseStaff.requests.bcuContingencias")} MN</span>
                                <span className="font-mono font-medium">$ {formatUYU(inst.breakdown.contingenciasMN)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">{t("enterpriseStaff.requests.bcuContingencias")} ME</span>
                                <span className="font-mono font-medium">$ {formatUYU(inst.breakdown.contingenciasME)}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
}

function CompactBcuResult({ result }: { result: Record<string, unknown> }): JSX.Element {
  const { t } = useTranslation();
  const [detailOpen, setDetailOpen] = useState(false);
  const hasDebts = result.hasDebts as boolean;
  const personName = (result.personName as string) ?? "";
  const activitySector = (result.activitySector as string) ?? "";
  const worstRating = (result.worstRating as string) ?? "";
  const summary = (result.summary ?? {}) as BcuBreakdown;
  const institutions = (result.institutions ?? []) as BcuInstitution[];
  const periodo = (result.periodo as string) ?? "";
  const periodoFormatted = periodo.length === 6 ? `${periodo.slice(4, 6)}/${periodo.slice(0, 4)}` : periodo;

  const detailModal = (
    <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("enterpriseStaff.requests.bcuDetailTitle")}</DialogTitle>
          {personName && <DialogDescription>{personName}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          {activitySector && (
            <p className="text-xs text-muted-foreground">{activitySector}</p>
          )}
          {periodo && (
            <p className="text-xs text-muted-foreground">
              {t("enterpriseStaff.requests.bcuPeriodo")}: {periodoFormatted}
            </p>
          )}
          {worstRating && (() => {
            const rc = ratingColor(worstRating);
            return (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{t("enterpriseStaff.requests.bcuRating")}:</span>
                <span className={`rounded px-2 py-0.5 text-sm font-bold ${rc.bg} ${rc.text} border ${rc.border}`}>
                  {worstRating}
                </span>
              </div>
            );
          })()}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border bg-card p-2.5">
              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{t("enterpriseStaff.requests.bcuVigente")}</p>
              <p className="mt-0.5 text-sm font-bold">$ {formatUYU(summary.vigenteMN)}</p>
              {summary.vigenteME > 0 && <p className="text-[11px] text-muted-foreground">ME $ {formatUYU(summary.vigenteME)}</p>}
            </div>
            <div className="rounded-lg border bg-card p-2.5">
              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{t("enterpriseStaff.requests.bcuContingencias")}</p>
              <p className="mt-0.5 text-sm font-bold">$ {formatUYU(summary.contingenciasMN)}</p>
              {summary.contingenciasME > 0 && <p className="text-[11px] text-muted-foreground">ME $ {formatUYU(summary.contingenciasME)}</p>}
            </div>
          </div>
          {institutions.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("enterpriseStaff.requests.bcuInstitutions")} ({institutions.length})
              </p>
              {institutions.map((inst, idx) => {
                const instRc = ratingColor(inst.rating);
                const instTotal = inst.breakdown.vigenteMN + inst.breakdown.vigenteME;
                return (
                  <div key={idx} className="rounded-lg border bg-card p-2.5">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="text-xs font-medium">{inst.name}</p>
                        <p className="text-[11px] text-muted-foreground">$ {formatUYU(instTotal)}</p>
                      </div>
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${instRc.bg} ${instRc.text} border ${instRc.border}`}>
                        {inst.rating}
                      </span>
                    </div>
                    <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("enterpriseStaff.requests.bcuVigente")} MN</span>
                        <span className="font-mono font-medium">$ {formatUYU(inst.breakdown.vigenteMN)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("enterpriseStaff.requests.bcuVigente")} ME</span>
                        <span className="font-mono font-medium">$ {formatUYU(inst.breakdown.vigenteME)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("enterpriseStaff.requests.bcuContingencias")} MN</span>
                        <span className="font-mono font-medium">$ {formatUYU(inst.breakdown.contingenciasMN)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("enterpriseStaff.requests.bcuContingencias")} ME</span>
                        <span className="font-mono font-medium">$ {formatUYU(inst.breakdown.contingenciasME)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  if (!hasDebts) {
    return (
      <>
        <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30 p-2">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600" />
          <p className="flex-1 text-xs font-medium text-green-700 dark:text-green-400">
            {t("enterpriseStaff.requests.bcuNoDebts")}
          </p>
          <button
            type="button"
            onClick={() => setDetailOpen(true)}
            className="text-[10px] font-medium text-primary hover:underline"
          >
            {t("enterpriseStaff.requests.bcuViewDetail")}
          </button>
        </div>
        {detailModal}
      </>
    );
  }

  const rc = ratingColor(worstRating);
  return (
    <>
      <div className={`flex items-center justify-between rounded-md border ${rc.border} ${rc.bg} p-2`}>
        <div className="flex items-center gap-2 min-w-0">
          <span className={`rounded px-2 py-0.5 text-sm font-bold ${rc.bg} ${rc.text} border ${rc.border}`}>
            {worstRating}
          </span>
          <div className="min-w-0">
            {personName && <p className="text-xs font-semibold truncate">{personName}</p>}
            <p className="text-[10px] text-muted-foreground">
              {t("enterpriseStaff.requests.bcuVigente")}: $ {formatUYU(summary.vigenteMN)}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDetailOpen(true)}
          className="text-[10px] font-medium text-primary hover:underline shrink-0"
        >
          {t("enterpriseStaff.requests.bcuViewDetail")}
        </button>
      </div>
      {detailModal}
    </>
  );
}

function MultiActorAutomationCard({
  subdomain,
  requestId,
  currentStep,
  data,
  typeDef,
  onActionComplete,
}: {
  subdomain: string;
  requestId: string;
  currentStep: ProductNode;
  data: AutomationNodeData;
  typeDef: (typeof AUTOMATION_TYPES)[number] | undefined;
  onActionComplete: () => void;
}): JSX.Element {
  const { t } = useTranslation();

  const participantsQuery = useQuery({
    queryKey: ["staff-automation-participants", subdomain, requestId],
    queryFn: () => getStaffRequestParticipants(subdomain, requestId),
  });

  const actors = (participantsQuery.data ?? []).filter((p) => {
    if (p.role === "initiator") return true;
    return p.status === "accepted" && p.inviteNodeId === data.actorSource;
  });

  const [actorResults, setActorResults] = useState<Record<string, {
    status: string;
    error?: string;
    data?: Record<string, unknown>;
  }>>({});
  const [runningActor, setRunningActor] = useState<number | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [actorInputs, setActorInputs] = useState<Record<number, Record<string, string>>>({});
  const [expandedActors, setExpandedActors] = useState<Set<number>>(new Set());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const completeMutation = useMutation({
    mutationFn: () => staffCompleteStep(subdomain, requestId, currentStep.id),
    onSuccess: () => onActionComplete(),
    onError: () => toast.error(t("enterpriseStaff.requests.actionError")),
  });

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const handleRunActor = async (actorIndex: number) => {
    setRunningActor(actorIndex);
    setElapsedSec(0);
    timerRef.current = setInterval(() => setElapsedSec((s) => s + 1), 1000);
    try {
      const inputs = actorInputs[actorIndex] ?? {};
      const res = await apiClient.post(
        `/enterprise/me/requests/${requestId}/run-automation/${currentStep.id}`,
        { runtimeInputs: inputs, actorIndex },
        { headers: { "X-Enterprise-Subdomain": subdomain } },
      );
      setActorResults((prev) => ({
        ...prev,
        [String(actorIndex)]: {
          status: res.data.status,
          error: res.data.error,
          data: res.data.data,
        },
      }));
      if (res.data.status === "success") {
        setExpandedActors((prev) => new Set(prev).add(actorIndex));
      }
    } catch {
      toast.error(t("enterpriseStaff.requests.automationError"));
    } finally {
      setRunningActor(null);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
  };

  const runtimeFields = (typeDef?.inputs ?? []).filter((inputDef) => {
    const mapping = data.inputMappings[inputDef.key];
    return !mapping || mapping.length === 0;
  });

  if (participantsQuery.isLoading) {
    return (
      <Card className="shadow-sm border-fuchsia-200 dark:border-fuchsia-900">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const getActorDisplayName = (p: StaffRequestParticipant) => {
    if (p.user) {
      const name = [p.user.firstName, p.user.lastName].filter(Boolean).join(" ").trim();
      return name || p.email;
    }
    return p.email;
  };

  return (
    <Card className="shadow-sm border-fuchsia-200 dark:border-fuchsia-900 bg-fuchsia-50/50 dark:bg-fuchsia-950/20">
      <CardContent className="space-y-4 py-5">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-fuchsia-100 dark:bg-fuchsia-900/30">
            <Bot className="h-4 w-4 text-fuchsia-600 dark:text-fuchsia-400" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-foreground">{data.label}</h3>
            {data.description && (
              <p className="text-xs text-muted-foreground">{data.description}</p>
            )}
          </div>
        </div>

        <div className="rounded-md border border-fuchsia-200 dark:border-fuchsia-800 bg-fuchsia-50 dark:bg-fuchsia-950/30 p-3">
          <p className="text-xs text-muted-foreground leading-relaxed">
            {t("enterpriseStaff.requests.automationPerActorHint")}
          </p>
        </div>

        <div className="space-y-2">
          {actors.map((actor, idx) => {
            const actorResult = actorResults[String(idx)];
            const isRunning = runningActor === idx;
            const isSuccess = actorResult?.status === "success";
            const isError = actorResult?.status === "error";
            const isExpanded = expandedActors.has(idx);
            const actorRuntimeInputs = actorInputs[idx] ?? {};
            const allActorFieldsFilled = runtimeFields
              .filter((f) => f.required)
              .every((f) => actorRuntimeInputs[f.key]?.trim());

            return (
              <div key={actor.id} className="rounded-lg border bg-card">
                <div className="flex items-center gap-3 p-3">
                  <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                    isSuccess ? "bg-green-100 dark:bg-green-900/30"
                    : isError ? "bg-red-100 dark:bg-red-900/30"
                    : "bg-muted"
                  }`}>
                    {isSuccess ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                    : isError ? <XCircle className="h-4 w-4 text-red-500" />
                    : <Clock className="h-4 w-4 text-muted-foreground" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{getActorDisplayName(actor)}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {actor.role === "initiator" ? "Solicitante" : actor.email}
                    </p>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    isSuccess ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    : isError ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                    : "bg-muted text-muted-foreground"
                  }`}>
                    {isSuccess ? t("enterpriseStaff.requests.automationActorSuccess")
                    : isError ? t("enterpriseStaff.requests.automationActorError")
                    : t("enterpriseStaff.requests.automationActorUnchecked")}
                  </span>
                  {isSuccess && (
                    <button
                      type="button"
                      onClick={() => setExpandedActors((prev) => {
                        const next = new Set(prev);
                        if (next.has(idx)) next.delete(idx); else next.add(idx);
                        return next;
                      })}
                      className="shrink-0"
                    >
                      {isExpanded
                        ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                    </button>
                  )}
                </div>

                {!isSuccess && !isRunning && (
                  <div className="border-t px-3 pb-3 pt-2 space-y-2">
                    {runtimeFields.map((inputDef) => (
                      <div key={inputDef.key} className="space-y-1">
                        <Label className="text-xs">{inputDef.label}</Label>
                        {inputDef.key === "periodo" ? (
                          <PeriodoSelect
                            value={actorRuntimeInputs[inputDef.key] ?? ""}
                            onChange={(v) => setActorInputs((prev) => ({
                              ...prev,
                              [idx]: { ...(prev[idx] ?? {}), [inputDef.key]: v },
                            }))}
                          />
                        ) : (
                          <Input
                            value={actorRuntimeInputs[inputDef.key] ?? ""}
                            onChange={(e) => setActorInputs((prev) => ({
                              ...prev,
                              [idx]: { ...(prev[idx] ?? {}), [inputDef.key]: e.target.value },
                            }))}
                            placeholder={inputDef.defaultDescription}
                          />
                        )}
                      </div>
                    ))}
                    {isError && actorResult?.error && (
                      <div className="rounded-md border border-destructive/20 bg-destructive/5 p-2">
                        <p className="text-xs text-destructive">{actorResult.error}</p>
                      </div>
                    )}
                    <Button
                      size="sm"
                      className="w-full bg-fuchsia-600 hover:bg-fuchsia-700"
                      onClick={() => handleRunActor(idx)}
                      disabled={!allActorFieldsFilled}
                    >
                      {isError
                        ? t("enterpriseStaff.requests.automationRetry")
                        : t("enterpriseStaff.requests.automationRun")}
                    </Button>
                  </div>
                )}

                {isRunning && (
                  <div className="border-t px-3 pb-3 pt-2 space-y-2">
                    <div className="flex items-center gap-3 rounded-md border border-fuchsia-200 dark:border-fuchsia-800 bg-fuchsia-50 dark:bg-fuchsia-950/30 p-2">
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-fuchsia-600" />
                      <p className="text-xs text-muted-foreground flex-1">
                        {elapsedSec < 10 ? t("enterpriseStaff.requests.automationStepLogin")
                        : elapsedSec < 30 ? t("enterpriseStaff.requests.automationStepCaptcha")
                        : t("enterpriseStaff.requests.automationStepQuery")}
                      </p>
                      <span className="text-xs font-mono text-muted-foreground">{elapsedSec}s</span>
                    </div>
                  </div>
                )}

                {isSuccess && isExpanded && actorResult?.data && (
                  <div className="border-t px-3 pb-3 pt-2">
                    <CompactBcuResult result={actorResult.data} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <Button
          variant="outline"
          className="w-full"
          size="sm"
          onClick={() => completeMutation.mutate()}
          disabled={completeMutation.isPending}
        >
          {completeMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t("enterpriseStaff.requests.automationCompleteStep")}
        </Button>
      </CardContent>
    </Card>
  );
}

function StaffActionsCard({
  subdomain,
  requestId,
  currentStep,
  currentStepLabel,
  currentStepActorLabel,
  isAwaitingChild,
  isPending,
  onActionComplete,
}: {
  subdomain: string;
  requestId: string;
  enterpriseId: string;
  currentStep?: ProductNode;
  currentStepLabel?: string;
  currentStepActorLabel?: string;
  isAwaitingChild?: boolean;
  isPending?: boolean;
  onActionComplete: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const pickUpMutation = useMutation({
    mutationFn: () => staffPickUpRequest(subdomain, requestId),
    onSuccess: () => {
      toast.success(t("enterpriseStaff.requests.pickUpSuccess"));
      onActionComplete();
    },
    onError: () => toast.error(t("enterpriseStaff.requests.actionError")),
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      setUploading(true);
      return staffUploadDocument(subdomain, requestId, currentStep!.id, file);
    },
    onSuccess: () => {
      toast.success(t("enterpriseStaff.requests.actionSuccess"));
      setUploading(false);
      onActionComplete();
    },
    onError: () => {
      toast.error(t("enterpriseStaff.requests.actionError"));
      setUploading(false);
    },
  });

  const completeMutation = useMutation({
    mutationFn: () => staffCompleteStep(subdomain, requestId, currentStep!.id),
    onSuccess: () => {
      toast.success(t("enterpriseStaff.requests.actionSuccess"));
      onActionComplete();
    },
    onError: () => toast.error(t("enterpriseStaff.requests.actionError")),
  });

  const infoMutation = useMutation({
    mutationFn: () => staffRequestInfo(subdomain, requestId, infoMessage || undefined),
    onSuccess: () => {
      toast.success(t("enterpriseStaff.requests.actionSuccess"));
      setInfoOpen(false);
      onActionComplete();
    },
    onError: () => toast.error(t("enterpriseStaff.requests.actionError")),
  });

  const isCurrentStepEnterprise =
    currentStep?.type === "staffApproval" || currentStep?.type === "staffAction";

  const staffData = currentStep?.data as { fileUpload?: boolean; fileLabel?: string } | undefined;
  const hasFileUpload = isCurrentStepEnterprise && staffData?.fileUpload === true;

  // When pending, show a pick-up card instead of the full actions
  if (isPending && isCurrentStepEnterprise) {
    return (
      <Card className="shadow-sm border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
        <CardContent className="space-y-3 py-5">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
              <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="min-w-0 space-y-0.5">
              <h3 className="text-sm font-semibold text-foreground">
                {t("enterpriseStaff.requests.pickUpTitle")}
              </h3>
              <p className="text-xs text-muted-foreground">
                {t("enterpriseStaff.requests.pickUpDescription")}
              </p>
            </div>
          </div>
          <Button
            className="w-full"
            size="sm"
            onClick={() => pickUpMutation.mutate()}
            disabled={pickUpMutation.isPending}
          >
            {pickUpMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <HandMetal className="mr-1.5 h-4 w-4" />
            {t("enterpriseStaff.requests.pickUpAction")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const infoDialog = (
    <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("enterpriseStaff.requests.requestInfoTitle")}</DialogTitle>
          <DialogDescription>
            {t("enterpriseStaff.requests.requestInfoDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Textarea
            value={infoMessage}
            onChange={(e) => setInfoMessage(e.target.value)}
            placeholder={t("enterpriseStaff.requests.requestInfoMessageLabel")}
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setInfoOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => infoMutation.mutate()}
            disabled={infoMutation.isPending}
          >
            {infoMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("enterpriseStaff.requests.actionRequestInfo")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Non-staff step: show a waiting card (user/participant needs to act)
  if (!isCurrentStepEnterprise) {
    return (
      <>
        <Card className="shadow-sm border-muted">
          <CardContent className="space-y-3 py-5">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
                <Clock className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="min-w-0 space-y-0.5">
                <h3 className="text-sm font-semibold text-foreground">
                  {currentStepLabel || t("enterpriseStaff.requests.actionComplete")}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {currentStepActorLabel
                    ? t("enterpriseStaff.requests.waitingForActorHint", { actor: currentStepActorLabel })
                    : t("enterpriseStaff.requests.waitingForUserHint")}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full"
              size="sm"
              onClick={() => setInfoOpen(true)}
            >
              <MessageSquare className="mr-1.5 h-4 w-4" />
              {t("enterpriseStaff.requests.actionRequestInfo")}
            </Button>
          </CardContent>
        </Card>
        {infoDialog}
      </>
    );
  }

  return (
    <>
      <Card className="shadow-sm border-primary/20">
        <CardContent className="space-y-3 pt-5">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 shrink-0 text-primary" />
            <p className="text-sm font-semibold text-foreground">
              {currentStepLabel || t("enterpriseStaff.requests.actionComplete")}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("enterpriseStaff.requests.pendingActionHint")}
          </p>
          <div className="flex gap-2">
            {isCurrentStepEnterprise && !hasFileUpload && (
              <Button
                className="flex-1"
                size="sm"
                onClick={() => completeMutation.mutate()}
                disabled={completeMutation.isPending || isAwaitingChild}
              >
                {completeMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <CheckCircle2 className="mr-1.5 h-4 w-4" />
                {t("enterpriseStaff.requests.actionApprove")}
              </Button>
            )}
            {hasFileUpload && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    uploadMutation.mutate(file);
                  }}
                />
                <Button
                  className="flex-1"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || isAwaitingChild}
                >
                  {uploading ? (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-1.5 h-4 w-4" />
                  )}
                  {t("enterpriseStaff.requests.uploadAndComplete")}
                </Button>
              </>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => setInfoOpen(true)}
            disabled={isAwaitingChild}
          >
            <MessageSquare className="mr-1.5 h-4 w-4" />
            {t("enterpriseStaff.requests.actionRequestInfo")}
          </Button>
        </CardContent>
      </Card>

      {infoDialog}
    </>
  );
}

const PARTICIPANT_STATUS_VARIANT: Record<string, StatusBadgeVariant> = {
  pending: "warning",
  accepted: "success",
  declined: "softDanger",
};

function ParticipantsCard({
  participants,
  graph,
  requester,
  workflowState,
}: {
  participants: StaffRequestParticipant[];
  graph: ProductGraph | null;
  requester?: { firstName?: string | null; lastName?: string | null; email: string } | null;
  workflowState?: GraphState | null;
}): JSX.Element {
  const { t } = useTranslation();

  // Resolve actor label from the inviteActors node in the graph
  function resolveActorLabel(inviteNodeId: string | null): string | null {
    if (!inviteNodeId || !graph) return null;
    const node = graph.nodes.find((n) => n.id === inviteNodeId && n.type === "inviteActors");
    if (!node) return null;
    const data = node.data as { actorLabel?: string };
    return data.actorLabel?.trim() || null;
  }

  // Find completed automation nodes with per-actor results
  const automationResults = useMemo(() => {
    if (!graph || !workflowState) return null;
    for (const node of graph.nodes) {
      if (node.type !== "automation") continue;
      const stepData = workflowState.completedNodes[node.id]?.data as Record<string, unknown> | undefined;
      if (!stepData?.results) continue;
      return {
        label: (node.data as unknown as AutomationNodeData).label,
        results: stepData.results as Record<string, { status: string; data?: Record<string, unknown> }>,
      };
    }
    return null;
  }, [graph, workflowState]);

  // Build unified actor list: requester first, then participants
  const allActors = useMemo(() => {
    const actors: Array<{
      key: string;
      name: string | null;
      email: string;
      roleLabel: string;
      status?: string;
      actorIndex: number;
      isRequester: boolean;
      firstName?: string | null;
      lastName?: string | null;
    }> = [];

    if (requester) {
      const name = [requester.firstName, requester.lastName].filter(Boolean).join(" ").trim();
      actors.push({
        key: "requester",
        name: name || null,
        email: requester.email,
        roleLabel: t("enterpriseStaff.requests.roleRequester"),
        actorIndex: 0,
        isRequester: true,
        firstName: requester.firstName,
        lastName: requester.lastName,
      });
    }

    const sortedParticipants = [...participants].sort((a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    sortedParticipants.forEach((p, idx) => {
      const name = p.user
        ? [p.user.firstName, p.user.lastName].filter(Boolean).join(" ").trim()
        : null;
      const actorLabel = resolveActorLabel(p.inviteNodeId);
      actors.push({
        key: p.id,
        name,
        email: p.user?.email ?? p.email,
        roleLabel: actorLabel ?? t("enterpriseStaff.requests.roleParticipant"),
        status: p.status,
        actorIndex: idx + 1,
        isRequester: false,
        firstName: p.user?.firstName,
        lastName: p.user?.lastName,
      });
    });

    return actors;
  }, [requester, participants, t]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          {t("enterpriseStaff.requests.participantsTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="divide-y divide-border">
          {allActors.map((actor) => {
            const bcuResult = automationResults?.results[String(actor.actorIndex)];
            const hasBcuData = bcuResult?.status === "success" && bcuResult.data;

            return (
              <div key={actor.key} className="py-3 first:pt-0 last:pb-0 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <UserAvatar
                      firstName={actor.firstName}
                      lastName={actor.lastName}
                      email={actor.email}
                      size="sm"
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {actor.name && (
                          <p className="text-sm font-medium text-foreground truncate">{actor.name}</p>
                        )}
                        <span className="text-[10px] text-muted-foreground shrink-0">
                          {actor.roleLabel}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {actor.email}
                      </p>
                    </div>
                  </div>
                  {actor.status && (
                    <StatusBadge variant={PARTICIPANT_STATUS_VARIANT[actor.status] ?? "info"}>
                      {t(`enterpriseStaff.requests.participantStatus.${actor.status}`)}
                    </StatusBadge>
                  )}
                </div>

                {hasBcuData && (
                  <div className="ml-11">
                    <CompactBcuResult result={bcuResult.data as Record<string, unknown>} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

const CHILD_STATUS_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  pending: "outline",
  assigned: "secondary",
  in_progress: "secondary",
  completed: "default",
  rejected: "destructive",
  cancelled: "destructive",
};

function ChildRequestsCard({
  childRequests,
}: {
  childRequests: ChildRequestSummary[];
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">
          {t("enterpriseStaff.requests.childRequestsTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {childRequests.map((child) => (
          <div
            key={child.childRequestId}
            className="rounded-md border border-border bg-muted/30 p-3"
          >
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-foreground">{child.type}</p>
                <p className="font-mono text-xs text-muted-foreground">
                  {child.childRequestId.slice(0, 8)}...
                </p>
              </div>
              <Badge variant={CHILD_STATUS_VARIANT[child.status] ?? "outline"}>
                {child.status}
              </Badge>
            </div>
            {child.documents && child.documents.length > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-xs font-medium text-muted-foreground">
                  {t("enterpriseStaff.requests.childDocuments")}
                </p>
                {child.documents.map((doc: ChildRequestDocument) => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {doc.fileName}
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
